import { useEffect, useState } from "react";
import { Loader2, Landmark, RefreshCw, Copy, FileDown, AlertTriangle, CheckCircle2, ShieldCheck, Search, Link2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ensureCapayAccount, getCapayAccountBySf, refreshCapayAccount, capayAccountPdfUrl,
  previewCapayAccount, searchCapayAccounts, linkCapayAccount, getSettlementOptions,
} from "@/lib/api";
import SettlementAccountSelect from "@/components/aus/SettlementAccountSelect";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const AU_STATE_CODES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

export function VaDetails({ va, sf, compact = false }) {
  const copy = async () => {
    const txt = `Account name: ${va.account_name}\nBSB: ${va.bsb}\nAccount number: ${va.account_number}\nBank: ${va.bank_name}\nReference: ${sf}`;
    try { await navigator.clipboard.writeText(txt); toast.success("Settlement account copied"); } catch { toast.error("Copy failed"); }
  };
  return (
    <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 p-4" data-testid="capay-va-details">
      <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-4"} gap-4 text-sm`}>
        <div><p className="text-muted-foreground text-xs">Account name</p><p className="font-medium" data-testid="capay-va-account-name">{va.account_name}</p></div>
        <div><p className="text-muted-foreground text-xs">BSB</p><p className="font-mono font-medium" data-testid="capay-va-bsb">{va.bsb}</p></div>
        <div><p className="text-muted-foreground text-xs">Account number</p><p className="font-mono font-medium" data-testid="capay-va-account-number">{va.account_number}</p></div>
        <div><p className="text-muted-foreground text-xs">Bank</p><p className="font-medium">{va.bank_name}</p></div>
      </div>
      {va.pay_id && <p className="text-xs mt-2">PayID: <span className="font-mono">{va.pay_id}</span></p>}
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={copy} data-testid="capay-va-copy-btn"><Copy className="w-3.5 h-3.5 mr-1" />Copy</Button>
        <a href={capayAccountPdfUrl(sf)} target="_blank" rel="noreferrer">
          <Button size="sm" variant="outline" data-testid="capay-va-pdf-btn"><FileDown className="w-3.5 h-3.5 mr-1" />Customer PDF</Button>
        </a>
        <span className="text-[11px] text-muted-foreground ml-auto flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />First-party payments only</span>
      </div>
    </div>
  );
}

const FIELD_LABELS = {
  abn: "ABN (11 digits)", legal_name: "Registered name", beneficial_owner_name: "Director (beneficial owner)",
  register_date: "Registration date", business_type: "Business type", street: "Street", city: "Suburb / city",
  state: "State", zip: "Postcode", acn: "ACN",
};
const FIELD_TYPES = { register_date: "date" };

function toPayload(sfNumber, form, user, applicationId, caseId) {
  return {
    sf_number: sfNumber, entity_kind: "business", is_sole_trader: !!form.is_sole_trader,
    company_name: form.company_name || form.legal_name, legal_name: form.legal_name, trading_name: form.trading_name,
    abn: form.abn, acn: form.acn, business_type: form.business_type, register_date: form.register_date,
    address: form.street || !form.address_line ? { street: form.street || "", city: form.city || "", state: form.state || "", zip: form.zip || "" }
                                               : { line: form.address_line, state: form.state || "", zip: form.zip || "" },
    contact_email: form.contact_email, phone: form.phone, beneficial_owner_name: form.beneficial_owner_name,
    application_id: applicationId || "", case_id: caseId || "",
  };
}

function Fact({ label, value, missing, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`text-sm ${missing ? "text-amber-700 italic" : "font-medium"}`}>{value || "missing"}</p>
    </div>
  );
}

function FindExisting({ sfNumber, busy, setBusy, onAccount, user }) {
  const [open, setOpen] = useState(false);
  const [findQ, setFindQ] = useState("");
  const [found, setFound] = useState(null);
  const find = async () => {
    if (findQ.trim().length < 2) return;
    setBusy(true);
    try { setFound((await searchCapayAccounts(findQ.trim())).accounts || []); } catch { toast.error("Search failed"); } finally { setBusy(false); }
  };
  const link = async (acc) => {
    setBusy(true);
    try { const r = await linkCapayAccount(acc.sf_number, sfNumber); onAccount?.(r); toast.success(`Linked ${sfNumber} to ${acc.sf_number}'s settlement account`); }
    catch (e) { toast.error(errorDetailMessage(e, "Link failed")); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" className="text-xs text-blue-700 hover:underline flex items-center gap-1" onClick={() => setOpen(true)} data-testid="capay-find-toggle"><Search className="w-3 h-3" />Existing customer (top-up / repeat deal)? Reuse their settlement account</button>;
  return (
    <div className="rounded-md bg-slate-50 border p-3 space-y-2" data-testid="capay-find-existing">
      <div className="flex gap-2">
        <Input value={findQ} onChange={(e) => setFindQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && find()} placeholder="Company name, ABN or SF number" className="h-8 text-sm" data-testid="capay-find-input" />
        <Button size="sm" variant="outline" onClick={find} disabled={busy} data-testid="capay-find-btn"><Search className="w-3.5 h-3.5" /></Button>
      </div>
      {found && (
        <div className="space-y-1">
          {found.map((a) => (
            <div key={a.sf_number} className="flex items-center justify-between text-xs bg-white border rounded px-2 py-1" data-testid={`capay-find-row-${a.sf_number}`}>
              <span><span className="font-mono">{a.sf_number}</span> · {a.borrower_name} · BSB {a.virtual_account?.bsb} / {a.virtual_account?.account_number}</span>
              <Button size="sm" variant="ghost" onClick={() => link(a)} disabled={busy} data-testid={`capay-link-btn-${a.sf_number}`}><Link2 className="w-3.5 h-3.5 mr-1" />Use for {sfNumber}</Button>
            </div>
          ))}
          {!found.length && <p className="text-xs text-muted-foreground">No existing settlement account matched.</p>}
        </div>
      )}
    </div>
  );
}

/** Create-or-find form shared by the wizard card and the customer-file tab.
 *  The server autocompletes from ABR/ASIC + LoanDisk — the user only sees a
 *  summary plus inputs for whatever is still missing. */
export function SettlementAccountForm({ sfNumber, initial, user, applicationId, caseId, onAccount }) {
  const [form, setForm] = useState(initial || {});
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editAll, setEditAll] = useState(false);
  const [error, setError] = useState("");

  const preview = async (base) => {
    setLoading(true);
    try {
      const r = await previewCapayAccount(toPayload(sfNumber, base, user, applicationId, caseId));
      setForm((f) => ({ ...f, ...r.form }));
      setMissing(r.missing_fields || []);
    } catch { setMissing([]); } finally { setLoading(false); }
  };
  useEffect(() => { preview(initial || {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sfNumber, initial?.abn]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = (k) => (
    <div key={k}>
      <Label className="text-xs">{FIELD_LABELS[k] || k}</Label>
      {k === "state" ? (
        <select value={form.state || ""} onChange={set("state")} className="h-8 w-full rounded-md border text-sm px-2" data-testid="capay-field-state">
          <option value="">—</option>{AU_STATE_CODES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : <Input value={form[k] || ""} onChange={set(k)} type={FIELD_TYPES[k] || "text"} className="h-8 text-sm" data-testid={`capay-field-${k}`} />}
    </div>
  );

  const create = async () => {
    setBusy(true); setError("");
    try {
      const acc = await ensureCapayAccount(toPayload(sfNumber, form, user, applicationId, caseId));
      onAccount?.(acc);
      toast.success(`Settlement account created — BSB ${acc.virtual_account?.bsb} / ${acc.virtual_account?.account_number}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Capay account creation failed"));
    } finally { setBusy(false); }
  };

  const address = [form.street, form.city, [form.state, form.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const addrMissing = ["street", "city", "state", "zip"].some((k) => missing.includes(k));
  const editable = editAll ? Object.keys(FIELD_LABELS) : missing;

  return (
    <div className="rounded-lg border bg-white p-4 space-y-4" data-testid="capay-create-form">
      <p className="text-sm font-medium">No settlement account yet for <span className="font-mono">{sfNumber}</span>.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="capay-form-loading"><Loader2 className="w-4 h-4 animate-spin" />Pulling details from ABR / ASIC and LoanDisk…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 rounded-md bg-slate-50 border p-3" data-testid="capay-form-summary">
            <Fact label="Registered name" value={form.legal_name} missing={missing.includes("legal_name")} testId="capay-fact-legal-name" />
            <Fact label={`ABN${form.acn ? " · ACN" : ""}`} value={[form.abn, form.acn].filter(Boolean).join(" · ")} missing={missing.includes("abn")} testId="capay-fact-abn" />
            <Fact label="Director" value={form.beneficial_owner_name} missing={missing.includes("beneficial_owner_name")} testId="capay-fact-director" />
            <Fact label="Address" value={address} missing={addrMissing} testId="capay-fact-address" />
            <Fact label="Registered" value={form.register_date} missing={missing.includes("register_date")} testId="capay-fact-register-date" />
            <Fact label="Type" value={form.is_sole_trader ? "Sole trader" : form.business_type} missing={missing.includes("business_type")} testId="capay-fact-type" />
          </div>

          {editable.length > 0 && (
            <div className="space-y-2" data-testid="capay-missing-fields">
              {!editAll && <p className="text-xs text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Couldn't find everything automatically — fill in the missing details:</p>}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{editable.map(input)}</div>
              {editable.includes("abn") && <Button size="sm" variant="outline" onClick={() => preview(form)} disabled={busy} data-testid="capay-sync-abn-btn"><Sparkles className="w-3.5 h-3.5 mr-1" />Look up ABN</Button>}
            </div>
          )}

          {error && <p className="text-sm text-red-700 flex items-start gap-1" data-testid="capay-create-error"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{error}</p>}
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={create} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="capay-create-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Landmark className="w-4 h-4 mr-2" />}Create CAPAY settlement account
            </Button>
            <button type="button" className="text-xs text-muted-foreground hover:underline" onClick={() => setEditAll((v) => !v)} data-testid="capay-edit-toggle">{editAll ? "Hide details" : "Edit details"}</button>
          </div>
        </>
      )}
      <FindExisting sfNumber={sfNumber} busy={busy} setBusy={setBusy} onAccount={onAccount} user={user} />
    </div>
  );
}

/** Wizard Step 3: settlement-account choice (CaPay house · Ebury · customer sub account) + create/find the sub account. */
export default function CapayAccountCard({ sfNumber, companyDetails, borrowerAddress, signingDirector, contactEmail,
  applicationId, user, onAccount, choice, setChoice, onOptions }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState(null);
  const [options, setOptions] = useState(null);
  const [mode, setMode] = useState("");
  const directorName = typeof signingDirector === "object" ? (signingDirector?.name || signingDirector?.full_name || "") : (signingDirector || "");
  const isSole = !!companyDetails?.is_sole_trader;

  const initial = {
    company_name: companyDetails?.company_name || "",
    legal_name: companyDetails?.legal_name || (isSole ? "" : companyDetails?.company_name) || "",
    trading_name: companyDetails?.trading_name || (isSole ? "" : companyDetails?.company_name) || "",
    abn: companyDetails?.abn || "", acn: companyDetails?.acn || "", is_sole_trader: isSole,
    business_type: companyDetails?.company_type || "", register_date: companyDetails?.registration_date || "",
    street: [borrowerAddress?.addressLine1, borrowerAddress?.addressLine2].filter(Boolean).join(" "),
    city: borrowerAddress?.suburb || "", state: borrowerAddress?.state || companyDetails?.state || "",
    zip: borrowerAddress?.postcode || companyDetails?.postcode || "",
    beneficial_owner_name: isSole ? (companyDetails?.legal_name || directorName) : directorName,
    contact_email: contactEmail || signingDirector?.email || "", phone: signingDirector?.phone || "",
  };

  const loadOptions = async (acc) => {
    try {
      const o = await getSettlementOptions({ sf_number: sfNumber || undefined });
      setOptions(o); onOptions?.(o);
      // default: the customer's own sub account when one exists, else CaPay (house)
      if (!choice || (choice === "settlement_account" && !acc?.virtual_account?.account_number && o.default !== "settlement_account")) setChoice?.(o.default);
      return o;
    } catch { return null; }
  };
  const load = async () => {
    setLoading(true);
    try {
      let acc = null;
      if (sfNumber) {
        const r = await getCapayAccountBySf(sfNumber);
        setMode(r.mode || ""); acc = r.account || null; setAccount(acc); onAccount?.(acc);
      }
      await loadOptions(acc);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sfNumber]);

  const refresh = async () => {
    setBusy(true);
    try { const acc = await refreshCapayAccount(sfNumber); setAccount(acc); onAccount?.(acc); await loadOptions(acc); toast.success("Refreshed from Capay"); }
    catch { toast.error("Refresh failed"); } finally { setBusy(false); }
  };
  const created = async (acc) => { setAccount(acc); onAccount?.(acc); const o = await loadOptions(acc); if (o) setChoice?.("settlement_account"); };
  const hasVa = !!account?.virtual_account?.account_number;

  return (
    <div data-testid="capay-account-card">
      <div className="flex items-center gap-2 mb-1">
        <Landmark className="w-4 h-4 text-emerald-700" />
        <h3 className="text-lg font-semibold">Settlement Account (repayments)</h3>
        {mode && <Badge variant="outline" className={mode === "live" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300"} data-testid="capay-mode-badge">{mode.toUpperCase()}</Badge>}
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        The FTLA prints the chosen account as the repayment destination. Customers can settle straight into CaPay; a dedicated
        settlement sub account is optional.
      </p>
      <div className="max-w-2xl mb-4">
        <Label className="text-xs">Repayment account on this contract</Label>
        {options ? (
          <SettlementAccountSelect options={options.options} value={choice} onChange={(v) => setChoice?.(v)} className="mt-1" testId="capay-settlement-choice"
            onCreate={hasVa ? undefined : () => setChoice?.("settlement_account")} />
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground text-sm mt-1"><Loader2 className="w-4 h-4 animate-spin" />Loading settlement options…</div>
        )}
      </div>

      {choice === "settlement_account" && (!sfNumber ? (
        <p className="text-sm text-amber-700 flex items-center gap-1" data-testid="capay-no-sf"><AlertTriangle className="w-4 h-4" />Enter the Funding Agreement Number (Step 2) first.</p>
      ) : loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Checking Capay for {sfNumber}…</div>
      ) : hasVa ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-emerald-800"><CheckCircle2 className="w-4 h-4" />
            Capay customer <span className="font-mono">{account.capay_client_no}</span> · <Badge variant="outline" data-testid="capay-client-status">{account.capay_client_status}</Badge>
            {account.sf_number !== sfNumber && <span className="text-xs text-muted-foreground">(shared with {account.sf_number})</span>}
            <Button size="sm" variant="ghost" onClick={refresh} disabled={busy} data-testid="capay-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /></Button>
          </div>
          <VaDetails va={account.virtual_account} sf={account.sf_number} />
        </div>
      ) : (
        <SettlementAccountForm sfNumber={sfNumber} initial={initial} user={user} applicationId={applicationId} onAccount={created} />
      ))}
    </div>
  );
}
